import styled, { css } from 'styled-components';
import React, { forwardRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useForwardedRef from '../../hooks/useForwardedRef';
import { SiteArticle } from '../../content/articles';
import { PROJECT_THEME } from '../../theme';
import {
    ArticleStyleProps,
    ArticleVariant,
    DEFAULT_ARTICLE_VARIANT,
    getArticleVariant,
} from './variants';
import { EdgeVariant, getEdgeVariant } from './edgeVariants';

type ArticleProps = Pick<SiteArticle, 'header' | 'title' | 'description' | 'tags' | 'url'>;

/* Falls back to the style variant's own (legacy) fadeEdges flag when no
   explicit edge treatment is given, so the 40-variant style lab is unaffected. */
const resolveEdge = (edge: number | undefined, style: ArticleVariant): EdgeVariant =>
    getEdgeVariant(edge) ?? { id: 0, label: '', note: '', fadeEdges: style.fadeEdges };

type StyledProps = ArticleStyleProps & { $v: ArticleVariant; $edge: EdgeVariant };

export type ScrollDirection = 'left' | 'right';

const RESUME_DELAY_MS = 1400;
const DRAG_SLOP_PX = 4;

const EDGE_FADE = 'linear-gradient(to right, transparent 0, black 7%, black 93%, transparent 100%)';

const fadeEdges = css`
    mask-image: ${EDGE_FADE};
    -webkit-mask-image: ${EDGE_FADE};
`;

const Rail = styled.div<StyledProps>`
    --card-w: ${({ $v }) => $v.cardWidth};
    --card-h: ${({ $v }) => $v.cardHeight};
    --gap: ${({ $v }) => $v.gap};
    position: relative;
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    user-select: none;
    cursor: grab;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }

    &[data-dragging='true'] {
        cursor: grabbing;
    }

    ${({ $edge }) => ($edge.fadeEdges ? fadeEdges : '')}
    ${({ $v }) => $v.rail}
    ${({ $edge }) => $edge.rail}

    @media (max-width: 55rem) {
        --card-w: 72vw;
    }
`;

const Track = styled.div<StyledProps>`
    display: flex;
    width: max-content;
    ${({ $v }) => $v.track}
`;

const Header = styled.h5<StyledProps>`
    margin: 0 0 0.5em 0;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 0.7em;
    font-weight: 400;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
    ${({ $v }) => $v.header}
`;

const Title = styled.h2<StyledProps>`
    margin: 0 0 0.5em 0;
    font-family: ${({ theme }) => theme.font.FONT_SERIF_ROBOTO_SERIF};
    font-weight: 700;
    font-size: 1.5em;
    line-height: 1.2;
    ${({ $v }) => $v.title}
`;

const Description = styled.p<StyledProps>`
    margin: 0;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 0.9em;
    font-weight: 400;
    line-height: 1.5;
    ${({ $v }) => $v.description}
`;

const Tags = styled.div<StyledProps>`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5em;
    margin-top: auto;
    padding-top: 1rem;
    ${({ $v }) => $v.tags}
`;

const Tag = styled.img<StyledProps>`
    height: 2.4em;
    width: auto;
    object-fit: contain;
    ${({ $v }) => $v.tag}
`;

const IndexPlate = styled.span<StyledProps>`
    position: absolute;
    top: 0.4em;
    right: 0.5em;
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 2.4em;
    line-height: 1;
    color: ${({ $accent }) => $accent};
    opacity: 0.2;
    ${({ $v }) => $v.number}
`;

const cardStyles = css<StyledProps>`
    position: relative;
    flex: 0 0 auto;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: var(--card-w);
    height: var(--card-h);
    /* Every card carries the gap, last one included, so one repeat is exactly
       count x this width and the loop can wrap by that without a visible jump. */
    margin-right: var(--gap);
    padding: 1.2em;
    overflow: hidden;
    background: ${({ theme }) => theme.colour.DARK_GREY};
    color: ${({ theme }) => theme.colour.OFF_WHITE};
    ${({ $v }) => $v.card}
    ${({ $edge }) => $edge.card}
`;

const Card = styled.div<StyledProps>`
    ${cardStyles}
`;

const CardLink = styled(Link)<StyledProps>`
    ${cardStyles}
    text-decoration: none;
`;

const Article = ({ article, variant, edge, index, duplicate }: {
    article: ArticleProps;
    variant: ArticleVariant;
    edge: EdgeVariant;
    index: number;
    duplicate?: boolean;
}) => {
    const palette = PROJECT_THEME.colour.PALETTE;
    const styleProps: StyledProps = {
        $v: variant,
        $edge: edge,
        $index: index,
        $accent: palette[index % palette.length],
    };

    const body = (
        <>
            {variant.numbered && (
                <IndexPlate {...styleProps}>{String(index + 1).padStart(2, '0')}</IndexPlate>
            )}
            <Header {...styleProps}>{article.header}</Header>
            <Title {...styleProps}>{article.title}</Title>
            <Description {...styleProps}>{article.description}</Description>
            <Tags {...styleProps}>
                {article.tags.map((tag) => (
                    <Tag key={tag} src={tag} alt="" draggable={false} {...styleProps} />
                ))}
            </Tags>
        </>
    );

    if (article.url) {
        return (
            <CardLink
                to={article.url}
                draggable={false}
                aria-hidden={duplicate}
                tabIndex={duplicate ? -1 : undefined}
                {...styleProps}
            >
                {body}
            </CardLink>
        );
    }

    return <Card aria-hidden={duplicate} {...styleProps}>{body}</Card>;
};

type Metrics = { repeats: number; period: number; pxPerSecond: number };

const INITIAL_METRICS: Metrics = { repeats: 1, period: 0, pxPerSecond: 0 };

/* Below these widths an "exact fit" rail targets fewer full cards than its
   desktop count, matching how the style variant's own --card-w media query
   scales down (see the Rail's 55rem breakpoint). */
const sizeToFitCount = (railWidth: number, desktopCount: number) => {
    if (railWidth < 640) return 1;
    if (railWidth < 1000) return Math.min(2, desktopCount);
    return desktopCount;
};

type ArticleSectionProps = React.HTMLAttributes<HTMLDivElement> & {
    articles: ArticleProps[];
    variant?: number;
    /* Style of the boundary a card is inevitably cut against; omit to keep
       the style variant's own (legacy) fadeEdges flag. */
    edge?: number;
    direction?: ScrollDirection;
    /* Scales px/second: 1 is the style variant's own pace, <1 slower, >1 faster. */
    rate?: number;
};

const ArticleSection = forwardRef<HTMLDivElement, ArticleSectionProps>(
    ({ articles, variant = DEFAULT_ARTICLE_VARIANT, edge, direction = 'left', rate = 1, ...rest }, ref) => {
        const config = getArticleVariant(variant);
        const edgeConfig = resolveEdge(edge, config);
        const railRef = useForwardedRef<HTMLDivElement>(ref);
        const count = Math.max(articles.length, 1);
        const [metrics, setMetrics] = useState<Metrics>(INITIAL_METRICS);
        const sizeToFit = edgeConfig.sizeToFit;

        useLayoutEffect(() => {
            const rail = railRef.current;
            if (!rail) return;

            const measure = () => {
                const card = rail.firstElementChild?.firstElementChild as HTMLElement | null;
                if (!card) return;

                if (sizeToFit) {
                    const gap = parseFloat(window.getComputedStyle(card).marginRight || '0');
                    const fitCount = sizeToFitCount(rail.clientWidth, sizeToFit);
                    const width = Math.max(120, rail.clientWidth / fitCount - gap);
                    rail.style.setProperty('--card-w', `${width}px`);
                }

                const cardWidth = card.offsetWidth
                    + parseFloat(window.getComputedStyle(card).marginRight || '0');
                const period = cardWidth * count;
                if (!period) return;

                /* Two repeats beyond what fills the rail: one of slack each side, so a
                   drag never hits the end of the scroll range before the loop wraps. */
                const repeats = Math.ceil(rail.clientWidth / period) + 2;
                const pxPerSecond = (cardWidth / config.secondsPerCard) * rate;

                setMetrics((current) => (
                    current.repeats === repeats
                    && current.period === period
                    && current.pxPerSecond === pxPerSecond
                        ? current
                        : { repeats, period, pxPerSecond }
                ));
            };

            measure();
            const observer = new ResizeObserver(measure);
            observer.observe(rail);
            return () => observer.disconnect();
        }, [railRef, count, config.secondsPerCard, rate, sizeToFit]);

        useEffect(() => {
            const rail = railRef.current;
            const { period, pxPerSecond } = metrics;
            if (!rail || !period) return;

            const velocity = direction === 'right' ? -pxPerSecond : pxPerSecond;
            const autoScrolls = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            rail.scrollLeft = period;

            let frame = 0;
            let previous = 0;
            let resumeAt = 0;
            let hovering = false;
            let pressing = false;
            let dragging = false;
            let pressX = 0;
            let scrollOrigin = 0;

            const wrap = () => {
                if (rail.scrollLeft >= period * 2) {
                    rail.scrollLeft -= period;
                    return -period;
                }
                if (rail.scrollLeft < period) {
                    rail.scrollLeft += period;
                    return period;
                }
                return 0;
            };

            const advance = (now: number) => {
                const elapsed = previous ? Math.min(now - previous, 100) : 0;
                previous = now;

                const held = pressing || (hovering && config.pauseOnHover !== false);
                if (!held && now >= resumeAt) {
                    rail.scrollLeft += velocity * (elapsed / 1000);
                }

                wrap();
                frame = requestAnimationFrame(advance);
            };

            const start = () => {
                if (frame || !autoScrolls) return;
                previous = 0;
                frame = requestAnimationFrame(advance);
            };

            const stop = () => {
                if (!frame) return;
                cancelAnimationFrame(frame);
                frame = 0;
            };

            const defer = () => {
                resumeAt = performance.now() + RESUME_DELAY_MS;
            };

            const onScroll = () => {
                if (!frame) wrap();
            };

            const onPointerDown = (event: PointerEvent) => {
                pressX = event.clientX;
                if (event.pointerType !== 'mouse' || event.button !== 0) return;
                pressing = true;
                dragging = false;
                scrollOrigin = rail.scrollLeft;
            };

            /* Capture only once the press turns into a real drag: capturing on press
               would retarget the click to the rail and swallow the card's link. */
            const onPointerMove = (event: PointerEvent) => {
                if (!pressing) return;

                const delta = event.clientX - pressX;
                if (!dragging) {
                    if (Math.abs(delta) <= DRAG_SLOP_PX) return;
                    dragging = true;
                    rail.setPointerCapture(event.pointerId);
                    rail.dataset.dragging = 'true';
                }

                rail.scrollLeft = scrollOrigin - delta;
                scrollOrigin += wrap();
            };

            const onPointerUp = (event: PointerEvent) => {
                if (!pressing) return;
                pressing = false;
                dragging = false;
                if (rail.hasPointerCapture(event.pointerId)) {
                    rail.releasePointerCapture(event.pointerId);
                }
                delete rail.dataset.dragging;
                defer();
            };

            const onCaptureLost = () => {
                pressing = false;
                dragging = false;
                delete rail.dataset.dragging;
            };

            /* A drag that finishes on top of a card would otherwise follow its link.
               Measured off the click itself, so a stale flag can never eat a real one. */
            const onClick = (event: MouseEvent) => {
                if (Math.abs(event.clientX - pressX) <= DRAG_SLOP_PX) return;
                event.preventDefault();
                event.stopPropagation();
            };

            const onEnter = () => { hovering = true; };
            const onLeave = () => { hovering = false; };

            const observer = new IntersectionObserver(
                ([entry]) => (entry.isIntersecting ? start() : stop()),
                { rootMargin: '200px' }
            );
            observer.observe(rail);

            rail.addEventListener('scroll', onScroll, { passive: true });
            rail.addEventListener('wheel', defer, { passive: true });
            rail.addEventListener('touchstart', defer, { passive: true });
            rail.addEventListener('pointerdown', onPointerDown);
            rail.addEventListener('pointermove', onPointerMove);
            rail.addEventListener('pointerup', onPointerUp);
            rail.addEventListener('pointercancel', onPointerUp);
            rail.addEventListener('lostpointercapture', onCaptureLost);
            rail.addEventListener('click', onClick, true);
            rail.addEventListener('mouseenter', onEnter);
            rail.addEventListener('mouseleave', onLeave);

            return () => {
                stop();
                observer.disconnect();
                rail.removeEventListener('scroll', onScroll);
                rail.removeEventListener('wheel', defer);
                rail.removeEventListener('touchstart', defer);
                rail.removeEventListener('pointerdown', onPointerDown);
                rail.removeEventListener('pointermove', onPointerMove);
                rail.removeEventListener('pointerup', onPointerUp);
                rail.removeEventListener('pointercancel', onPointerUp);
                rail.removeEventListener('lostpointercapture', onCaptureLost);
                rail.removeEventListener('click', onClick, true);
                rail.removeEventListener('mouseenter', onEnter);
                rail.removeEventListener('mouseleave', onLeave);
            };
        }, [railRef, metrics, direction, config.pauseOnHover]);

        const cards = useMemo(() => (
            Array.from({ length: metrics.repeats * count }, (_, position) => ({
                article: articles[position % count],
                index: position % count,
                duplicate: position >= count,
            }))
        ), [articles, count, metrics.repeats]);

        const styleProps: StyledProps = {
            $v: config,
            $edge: edgeConfig,
            $index: 0,
            $accent: PROJECT_THEME.colour.PALETTE[0],
        };

        return (
            <Rail ref={railRef} {...styleProps} {...rest}>
                <Track {...styleProps}>
                    {cards.map((card, position) => (
                        <Article
                            key={position}
                            article={card.article}
                            variant={config}
                            edge={edgeConfig}
                            index={card.index}
                            duplicate={card.duplicate}
                        />
                    ))}
                </Track>
            </Rail>
        );
    }
);

ArticleSection.displayName = 'ArticleSection';

export { ArticleSection, Article };
